import { memo, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import hljs from "highlight.js/lib/core";

// Only the languages this stack actually produces. The full highlight.js bundle
// is ~1 MB and would be parsed on a phone for no benefit.
import bash from "highlight.js/lib/languages/bash";
import powershell from "highlight.js/lib/languages/powershell";
import json from "highlight.js/lib/languages/json";
import ts from "highlight.js/lib/languages/typescript";
import js from "highlight.js/lib/languages/javascript";
import python from "highlight.js/lib/languages/python";
import yaml from "highlight.js/lib/languages/yaml";
import sql from "highlight.js/lib/languages/sql";
import xml from "highlight.js/lib/languages/xml";
import ini from "highlight.js/lib/languages/ini";
import diff from "highlight.js/lib/languages/diff";

for (const [name, def] of Object.entries({
  bash,
  shell: bash,
  sh: bash,
  powershell,
  ps1: powershell,
  json,
  typescript: ts,
  ts,
  javascript: js,
  js,
  python,
  py: python,
  yaml,
  yml: yaml,
  sql,
  xml,
  html: xml,
  ini,
  toml: ini,
  diff,
})) {
  if (!hljs.getLanguage(name)) hljs.registerLanguage(name, def as never);
}

function CodeBlock({ language, code }: { language: string; code: string }) {
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLElement>(null);

  const html = useMemo(() => {
    if (language && hljs.getLanguage(language)) {
      try {
        return hljs.highlight(code, { language, ignoreIllegals: true }).value;
      } catch {
        /* fall through to plain text */
      }
    }
    return null;
  }, [code, language]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(t);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      // Clipboard needs a secure context; plain-http LAN loses it on some
      // browsers. Select the text so the user can copy by hand.
      const el = ref.current;
      if (!el) return;
      const range = document.createRange();
      range.selectNodeContents(el);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  };

  return (
    <div className="code">
      <div className="code__bar">
        <span className="code__lang">{language || "text"}</span>
        <button type="button" className="code__copy" onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="code__pre scroll">
        {html ? (
          <code ref={ref} className="hljs" dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <code ref={ref} className="hljs">
            {code}
          </code>
        )}
      </pre>
    </div>
  );
}

/**
 * Assistant text as real markdown.
 *
 * The old build rendered replies with `p.textContent += delta`, so every code
 * block, table, and list arrived as flat grey prose. That single line is the
 * biggest reason the chat felt worse than the desktop apps.
 */
export const Markdown = memo(function Markdown({ text }: { text: string }) {
  return (
    <div className="md">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code(props) {
            const { className, children, ...rest } = props;
            const raw = String(children ?? "");
            const lang = /language-(\w+)/.exec(className ?? "")?.[1] ?? "";
            // react-markdown marks fenced blocks by wrapping them in <pre>;
            // an inline span has no language class and no trailing newline.
            const isBlock = raw.includes("\n") || Boolean(lang);
            if (!isBlock) {
              return (
                <code className="md__inline" {...rest}>
                  {children}
                </code>
              );
            }
            return <CodeBlock language={lang} code={raw.replace(/\n$/, "")} />;
          },
          pre({ children }) {
            // CodeBlock brings its own <pre>; avoid nesting two.
            return <>{children}</>;
          },
          a({ href, children }) {
            return (
              <a href={href} target="_blank" rel="noreferrer noopener">
                {children}
              </a>
            );
          },
          table({ children }) {
            return (
              <div className="md__tablewrap scroll">
                <table>{children}</table>
              </div>
            );
          },
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
});
